import { ref, watchEffect, type Ref } from 'vue';
import { ResourceLoader } from '@/IsekaiRPG/shared/resources/resourceLoader';
import { LogService } from '@/IsekaiRPG/shared/logging/logService';

/**
 * useImageResource 返回值接口
 */
export interface UseImageResourceReturn {
  /** 当前图片 URL（加载完成后为 Blob URL） */
  url: Ref<string>;
  /** 是否正在加载 */
  isLoading: Ref<boolean>;
}

/**
 * 图片资源加载 Composable
 * 自动从 ResourceLoader 获取图片（优先使用缓存的 Blob URL）
 *
 * @param url 图片原始 URL
 * @returns 响应式的图片 URL 和加载状态
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useImageResource } from '@/IsekaiRPG/shared/composables/useImageResource';
 *
 * const props = defineProps<{
 *   imageUrl: string;
 * }>();
 *
 * const { url, isLoading } = useImageResource(props.imageUrl);
 * </script>
 *
 * <template>
 *   <img v-if="!isLoading" :src="url" />
 *   <span v-else>加载中...</span>
 * </template>
 * ```
 */
export function useImageResource(url: string): UseImageResourceReturn {
  const currentUrl = ref(url);
  const isLoading = ref(true);

  watchEffect(async () => {
    if (!url) return;

    try {
      isLoading.value = true;
      const blobUrl = await ResourceLoader.loadImage(url);
      currentUrl.value = blobUrl;
    } catch (error) {
      LogService.error('ImageResource', `图片加载失败: ${url}`, error as Error);
      currentUrl.value = url;
    } finally {
      isLoading.value = false;
    }
  });

  return {
    url: currentUrl,
    isLoading,
  };
}
